import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { keyBy } from "lodash";
import { useCallback } from "react";
import { IBusiness } from "@/business/business";
import { useGetBusiness } from "@/business/query";
import { useApi } from "@/core/api";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { dayjs } from "@/core/utils/date";
import { getDateRangeFromTimeFrame } from "@/core/utils/date";
import { invalidateCustomerQueries } from "@/customer/query";
import { referralsKey } from "@/referrals/query";
import { USER_ROLES } from "./constants";
import { userKey, businessUsersKey, userStatsKey } from "./query-keys";
import {
  DetailedUser,
  DetailedUserWithBusiness,
  UserStatsResponseItem,
} from "./user";
import { getEnrichedUser } from "./utils";

export { userKey, businessUsersKey, userStatsKey } from "./query-keys";

export const useGetMyUser = () => {
  const api = useApi();
  const { user } = useAuth();

  return useQuery({
    queryKey: userKey(user?.id),
    queryFn: async (): Promise<DetailedUser> =>
      api.get(`/users/${user?.id}`).then((res) => getEnrichedUser(res.data)),
    enabled: !!user?.id,
  });
};

export const useGetMyUserWithBusiness = () => {
  const { data: user } = useGetMyUser();
  const { businessId } = useBusiness();

  return useGetBusiness(
    businessId,
    useCallback(
      (business: IBusiness): DetailedUserWithBusiness => ({
        ...(user as DetailedUser),
        business,
      }),
      [user],
    ),
  );
};

export const useGetBusinessUsers = <TData = DetailedUser[]>(
  business_id?: string | null,
  options?: Omit<
    UseQueryOptions<DetailedUser[], Error, TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const api = useApi();

  const params = new URLSearchParams();
  if (business_id) params.set("business_id", business_id);

  return useQuery({
    queryKey: businessUsersKey(business_id),
    queryFn: async (): Promise<DetailedUser[]> =>
      api
        .get("/users", { params })
        .then(
          (res) =>
            res.data
              .filter((user: DetailedUser) => !user.discarded_at)
              .map(getEnrichedUser) as DetailedUser[],
        ),
    enabled: !!business_id && (options?.enabled ?? true),
    ...options,
  });
};

export const useGetBusinessUser = (
  business_id?: string | null,
  user_id?: string | null,
) =>
  useGetBusinessUsers(business_id, {
    enabled: !!user_id,
    select: useCallback(
      (users: DetailedUser[]) => users.find((user) => user.id === user_id),
      [user_id],
    ),
  });

export const useGetRepresentatives = (business_id?: string | null) =>
  useGetBusinessUsers(business_id, {
    select: useCallback(
      (users: DetailedUser[]) =>
        users.filter((user) => user.role === USER_ROLES.REP),
      [],
    ),
  });

export const useGetWebAppUsers = (business_id?: string | null) =>
  useGetBusinessUsers(business_id, {
    select: useCallback(
      (users: DetailedUser[]) =>
        users.filter((user) =>
          [
            USER_ROLES.OWNER,
            USER_ROLES.MANAGER,
            USER_ROLES.REP,
            USER_ROLES.AP_ONLY,
          ].includes(user.role),
        ),
      [],
    ),
  });

export const useGetTechnicians = (business_id?: string | null) =>
  useGetBusinessUsers(business_id, {
    select: useCallback(
      (users: DetailedUser[]) =>
        users.filter((user) => user.role === USER_ROLES.TECH),
      [],
    ),
  });

export const useGetNonAdminUsers = (business_id?: string | null) =>
  useGetBusinessUsers(business_id, {
    select: useCallback(
      (users: DetailedUser[]) =>
        users.filter((user) => user.role !== USER_ROLES.ADMIN),
      [],
    ),
  });

export const useEditMyUser = () => {
  const api = useApi();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["edit-user", user?.id],
    mutationFn: async (newUser: Partial<DetailedUser>) => {
      const { data } = await api.put(`/users/${user?.id}`, newUser);
      return data;
    },
    onSuccess: (updatedUser) => {
      const enrichedUser = getEnrichedUser(updatedUser);
      // Update the user cache
      queryClient.setQueryData(userKey(user?.id), enrichedUser);

      // update the users list respone
      queryClient.setQueryData(
        businessUsersKey(user?.parentable_id),
        (oldData: DetailedUser[]) => {
          if (!oldData) return;
          return oldData.map((user) =>
            user.id === enrichedUser.id ? enrichedUser : user,
          );
        },
      );

      invalidateCustomerQueries(queryClient, user?.parentable_id);
      queryClient.invalidateQueries({
        queryKey: referralsKey(user?.parentable_id),
      });
    },
  });
};

export const useUpsertUser = (business_id: string, isTechnician = false) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: Partial<DetailedUser>) =>
      user.id
        ? api.put(`users/${user.id}`, user)
        : api.post(`users`, {
            ...user,
            business_id,
            role: isTechnician ? USER_ROLES.TECH : user.role,
          }),
    onSuccess: (response, variables) => {
      const enrichedUser = getEnrichedUser(response.data);
      // Update all relevant query caches
      const queryKeys = [businessUsersKey(business_id)];

      queryKeys.forEach((queryKey) => {
        queryClient.setQueryData<DetailedUser[]>(queryKey, (oldData) => {
          if (!oldData) return oldData;

          if (variables.id) {
            return oldData.map((user) =>
              user.id === variables.id ? { ...user, ...enrichedUser } : user,
            );
          } else {
            return [...oldData, enrichedUser];
          }
        });
      });

      invalidateCustomerQueries(queryClient, business_id);
      queryClient.invalidateQueries({ queryKey: referralsKey(business_id) });
    },
  });
};

export const useGetUserStatsByBusiness = (
  business_id?: string | null,
  time_frame?: string,
) => {
  const api = useApi();

  return useQuery({
    queryKey: userStatsKey(business_id, time_frame),
    queryFn: async () => {
      const params = new URLSearchParams();

      const dateRange = getDateRangeFromTimeFrame(time_frame);
      if (dateRange) {
        params.set("start_date", dateRange.startDate.toISOString());
        params.set("end_date", dateRange.endDate.toISOString());
      }

      return api
        .get<UserStatsResponseItem[]>(`businesses/${business_id}/user_stats`, {
          params,
        })
        .then(
          (res) =>
            keyBy(res.data, "id") as Record<string, UserStatsResponseItem>,
        );
    },
    enabled: !!business_id,
  });
};

export const useGetUserStatsByBusinessWithTimeRange = (
  business_id?: string | null,
  timeRange?: { from: Date; to: Date } | null,
) => {
  const api = useApi();

  return useQuery({
    queryKey: [
      "users",
      "stats",
      business_id,
      timeRange?.from?.toISOString(),
      timeRange?.to?.toISOString(),
    ],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (timeRange) {
        // Convert from date to start of day (UTC) and to date to end of day (UTC)
        const startDate = dayjs(timeRange.from).utc().startOf("day").toDate();
        const endDate = dayjs(timeRange.to).utc().endOf("day").toDate();

        params.set("start_date", startDate.toISOString());
        params.set("end_date", endDate.toISOString());
      }

      return api
        .get<UserStatsResponseItem[]>(`businesses/${business_id}/user_stats`, {
          params,
        })
        .then(
          (res) =>
            keyBy(res.data, "id") as Record<string, UserStatsResponseItem>,
        );
    },
    enabled: !!business_id,
  });
};

export const useGetUsersWithStats = (
  business_id?: string | null,
  time_frame?: string,
) => {
  const { data: userStats, isLoading: isLoadingUserStats } =
    useGetUserStatsByBusiness(business_id, time_frame);

  const businessUsersQuery = useGetBusinessUsers(business_id, {
    select: useCallback(
      (users: DetailedUser[]) =>
        users.map((user) => ({
          ...user,
          stats: userStats?.[user.id]?.stats,
        })),
      [userStats],
    ),
  });

  return {
    ...businessUsersQuery,
    isLoading: isLoadingUserStats || businessUsersQuery.isLoading,
  };
};

export const useDeleteUser = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId?: string | null) => {
      if (!userId) return;
      return api.delete(`users/${userId}`);
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: userKey(userId) });
      queryClient.invalidateQueries({ queryKey: businessUsersKey(businessId) });
      invalidateCustomerQueries(queryClient, businessId);
      queryClient.invalidateQueries({ queryKey: referralsKey(businessId) });
    },
  });
};
